import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Paper, Typography, Table, TableBody, TableHead, TableRow, TableCell,
  CircularProgress, Alert, Autocomplete, TextField, Chip, Snackbar, TableContainer,
} from "@mui/material";
import api from "../services/api";

const price = (n) =>
  (Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 4, maximumFractionDigits: 4 });

export default function ProductPriceMap() {
  const [rows, setRows] = useState([]);
  const [canonicals, setCanonicals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState("");

  const load = useCallback(() => {
    setLoading(true); setError(null);
    Promise.all([
      api.get("/priceMappings/products"),
      api.get("/priceMappings/canonicals"),
    ])
      .then(([p, c]) => {
        setRows(p.data || []);
        setCanonicals((c.data || []).map((x) => x.CanonicalName).filter(Boolean));
      })
      .catch((e) => setError(e.response?.data?.error || "Failed to load product links"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const link = (itemId, canonicalName) => {
    api.post("/priceMappings/products", { Item_ID: itemId, CanonicalName: canonicalName || "" })
      .then(() => {
        setToast(canonicalName ? "Link saved" : "Link cleared");
        load();
      })
      .catch((e) => setError(e.response?.data?.error || "Failed to save link"));
  };

  const mappedCount = rows.filter((r) => r.CanonicalName).length;

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>Market Price Link</Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        Link each material to the commodity price it tracks. Once linked, the <strong>Purchase Invoice</strong> screen
        shows today's market price next to the buy rate, so you can see the spread you're locking in. This is a
        one-time setup per material.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}><CircularProgress /></Box>
      ) : (
        <Paper sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Chip sx={{ mb: 1.5 }} color={mappedCount ? "success" : "default"}
            label={`${mappedCount} of ${rows.length} materials linked`} />
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Material</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 260 }}>Linked commodity price</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Today's market</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 && (
                  <TableRow><TableCell colSpan={3} sx={{ color: "text.secondary" }}>No products found.</TableCell></TableRow>
                )}
                {rows.map((r) => (
                  <TableRow key={r.Item_ID} hover>
                    <TableCell sx={{ py: 0.4 }}>{r.Product_Description}</TableCell>
                    <TableCell sx={{ py: 0.4 }}>
                      <Autocomplete
                        options={canonicals}
                        value={r.CanonicalName || null}
                        onChange={(e, v) => link(r.Item_ID, v)}
                        size="small"
                        renderInput={(p) => <TextField {...p} placeholder="— not linked —" />}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ py: 0.4, fontVariantNumeric: "tabular-nums" }}>
                      {r.CanonicalName == null ? "—"
                        : r.MarketPrice == null ? <em style={{ color: "#999" }}>no price</em>
                        : `$${price(r.MarketPrice)}`}
                      {r.MarketDate && (
                        <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>
                          {String(r.MarketDate).slice(0, 10)}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Snackbar open={!!toast} autoHideDuration={2000} onClose={() => setToast("")} message={toast} />
    </Box>
  );
}
